/**
 * FERP Portal - Subgraph Entity Data Hook
 *
 * Repository-pattern hook for fetching entity data per subgraph + entity type.
 * All data comes from the backend via GraphQL. No mock fallbacks.
 *
 * FERP Principles:
 * - Repository pattern: No direct fetch() calls
 * - Data-driven: Row shape derived from SubgraphEntityType metadata
 * - Dumb frontend: Hook fetches, component renders
 * - Life-critical: Real data only, no synthetic/mock data
 */

import { useState, useEffect, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type { SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import { useTenantId, useAuth } from '@/auth/AuthContext';

export interface UseSubgraphEntityDataResult {
  data: Record<string, unknown>[];
  totalCount: number;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Derive list query name from entity type metadata.
 * Subgraph-prefixed types: FhirPatient → fhirPatients, ErpDocument → erpDocuments
 * Core types: Task → listTasks */
function getListQueryName(entityType: SubgraphEntityType): string {
  const { graphqlType, name } = entityType;
  // Subgraph-prefixed types use lcfirst(graphqlType) + 's' (DRR-052)
  if (graphqlType !== name) {
    return graphqlType.charAt(0).toLowerCase() + graphqlType.slice(1) + 's';
  }
  return `list${name}s`;
}

/** Extract first array field from GraphQL result */
function extractArrayFromResult(result: Record<string, unknown> | null | undefined): Record<string, unknown>[] {
  if (!result || typeof result !== 'object') return [];
  const values = Object.values(result);
  for (const value of values) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      return value;
    }
    // Handle connection types (e.g., { items: [...], nextCursor: "..." })
    if (typeof value === 'object' && 'items' in (value as Record<string, unknown>)) {
      const items = (value as Record<string, unknown>).items;
      if (Array.isArray(items)) {
        return items;
      }
    }
  }
  return [];
}

export function useSubgraphEntityData(
  subgraphId: string,
  entityType: SubgraphEntityType | null,
): UseSubgraphEntityDataResult {
  const client = useApolloClient();
  const tenantId = useTenantId();
  const { tenant } = useAuth();
  const tenantNum = tenant?.num ?? 0;
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!entityType || !tenantId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const listQueryName = getListQueryName(entityType);
      const isSubgraphPrefixed = entityType.graphqlType !== entityType.name;

      // Deduplicate fields (id may appear in both displayField and identifierFields)
      const fields = new Set(['id']);
      if (entityType.displayField) fields.add(entityType.displayField);
      for (const f of entityType.identifierFields) fields.add(f);
      const fieldList = [...fields].join('\n        ');

      // Subgraph queries: flat array return, requires tenantNum: Int! (DRR-052)
      // Core queries: items wrapper, no explicit tenantId param
      const query = isSubgraphPrefixed
        ? gql`
          query ${listQueryName}($tenantNum: Int!, $limit: Int) {
            ${listQueryName}(tenantNum: $tenantNum, limit: $limit) {
              ${fieldList}
            }
          }
        `
        : gql`
          query List${entityType.name}s($limit: Int) {
            ${listQueryName}(limit: $limit) {
              items {
                ${fieldList}
              }
            }
          }
        `;

      const variables = isSubgraphPrefixed
        ? { tenantNum, limit: 50 }
        : { limit: 50 };

      const result = await client.query<Record<string, any>>({
        query,
        variables,
        fetchPolicy: 'network-only',
      });

      const items = extractArrayFromResult(result.data);
      setData(items);

    } catch (err) {
      console.error(`Failed to fetch data for ${entityType.name}:`, err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [client, subgraphId, entityType, tenantId, tenantNum]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    totalCount: data.length,
    loading,
    error,
    refetch: fetchData,
  };
}

export default useSubgraphEntityData;
